'use client'

import { ChevronLeft, ChevronRight, ExternalLink, Play, ThumbsUp } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import type { MetaAdsFetchResult } from 'sprites-types/workflow/tools/metaAdsFetch'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'

type MetaAd = MetaAdsFetchResult[number]

interface MetaAdsItemProps {
  ad: MetaAd
  className?: string
}

type MediaItem =
  | { type: 'image'; src: string }
  | { type: 'video'; src: string; poster: string | null }
  | {
      type: 'card'
      image: string | null
      title: string | null
      description: string | null
      cta: string | null
      url: string | null
    }

function getHostname(url: string): string {
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

export function MetaAdsItem({ ad, className }: MetaAdsItemProps) {
  const [activeMedia, setActiveMedia] = useState(0)
  const { snapshot } = ad

  const startDate = new Date(ad.start_date * 1000)
  const endDate = new Date(ad.end_date * 1000)
  const dateLabel = `${startDate.toLocaleDateString()} – ${endDate.toLocaleDateString()}`
  const adLibraryUrl = `https://www.facebook.com/ads/library/?id=${ad.ad_archive_id}`

  // Build a unified media carousel: images → videos → cards
  const mediaItems: MediaItem[] = [
    ...snapshot.images
      .map((img) => img.resized_image_url ?? img.original_image_url ?? null)
      .filter((src): src is string => typeof src === 'string' && src.length > 0)
      .map((src): MediaItem => ({ type: 'image', src })),

    ...snapshot.videos
      .map((v) => ({
        src:
          v.video_sd_url ??
          v.video_hd_url ??
          v.watermarked_video_sd_url ??
          v.watermarked_video_hd_url ??
          null,
        poster: v.video_preview_image_url ?? null,
      }))
      .filter((v): v is { src: string; poster: string | null } => typeof v.src === 'string')
      .map((v): MediaItem => ({ type: 'video', src: v.src, poster: v.poster })),

    ...snapshot.cards.map((c): MediaItem => {
      // Cards with a playable video → treat as a video item
      const videoSrc =
        c.video_sd_url ??
        c.video_hd_url ??
        c.watermarked_video_sd_url ??
        c.watermarked_video_hd_url ??
        null
      if (videoSrc) {
        return {
          type: 'video',
          src: videoSrc,
          poster: c.video_preview_image_url ?? null,
        }
      }
      // Cards with images → card tile
      return {
        type: 'card',
        image: c.original_image_url ?? c.resized_image_url ?? null,
        title: c.title ?? null,
        description: c.link_description ?? null,
        cta: c.cta_text ?? null,
        url: c.link_url ?? null,
      }
    }),
  ]

  const activeItem = mediaItems[activeMedia] ?? null

  const contextActions: ContextAction[] = [
    {
      id: 'open-ad-library',
      label: 'View in Ad Library',
      icon: <ExternalLink className="h-4 w-4" />,
      onClick: () => window.open(adLibraryUrl, '_blank'),
    },
    ...(snapshot.link_url
      ? [
          {
            id: 'open-destination',
            label: 'Open Destination',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => window.open(snapshot.link_url ?? undefined, '_blank'),
          },
        ]
      : []),
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <article
        className={`overflow-hidden rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
      >
        {/* Page header */}
        <div className="flex items-center gap-2 px-4 pt-3">
          {snapshot.page_profile_picture_url && (
            <Image
              src={snapshot.page_profile_picture_url}
              alt="Page profile"
              width={24}
              height={24}
              className="rounded-full"
              unoptimized
            />
          )}
          <span className="flex-1 truncate text-sm font-semibold text-gray-900">
            {snapshot.title || 'Meta Ad'}
          </span>
        </div>

        {/* Body text */}
        {snapshot.body.text && (
          <div className="px-4 pt-2 text-sm leading-relaxed text-gray-700">
            {snapshot.body.text}
          </div>
        )}

        {/* Media carousel */}
        {mediaItems.length > 0 && (
          <div className="mt-3 px-4">
            {activeItem?.type === 'image' && (
              <Image
                src={activeItem.src}
                alt={`Ad media ${activeMedia + 1}`}
                width={800}
                height={450}
                className="w-full rounded-xl border border-gray-100 bg-gray-50 object-contain"
                style={{ maxHeight: '400px' }}
                unoptimized
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}

            {activeItem?.type === 'video' && (
              <>
                {activeItem.src ? (
                  <video
                    src={activeItem.src}
                    poster={activeItem.poster ?? undefined}
                    controls
                    className="w-full rounded-xl border border-gray-100 bg-black"
                    style={{ maxHeight: '400px' }}
                  />
                ) : activeItem.poster ? (
                  /* No playable src — show preview thumbnail with play overlay */
                  <div className="relative">
                    <Image
                      src={activeItem.poster}
                      alt={`Video preview ${activeMedia + 1}`}
                      width={800}
                      height={450}
                      className="w-full rounded-xl border border-gray-100 bg-gray-50 object-contain"
                      style={{ maxHeight: '400px' }}
                      unoptimized
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Play className="h-10 w-10 text-white drop-shadow-lg" />
                    </div>
                  </div>
                ) : null}
              </>
            )}

            {activeItem?.type === 'card' && (
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                {activeItem.image && (
                  <Image
                    src={activeItem.image}
                    alt={activeItem.title ?? 'Card'}
                    width={800}
                    height={300}
                    className="mb-2 w-full rounded-lg object-cover"
                    style={{ maxHeight: '200px' }}
                    unoptimized
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                )}
                {activeItem.title && (
                  <div className="mb-0.5 text-sm font-semibold text-gray-900">
                    {activeItem.title}
                  </div>
                )}
                {activeItem.description && (
                  <div className="mb-2 text-xs text-gray-600">{activeItem.description}</div>
                )}
                {activeItem.url && (
                  <a
                    href={activeItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2 py-0.5 text-xs text-blue-700 hover:bg-gray-50"
                  >
                    {activeItem.cta ?? getHostname(activeItem.url)}
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* Caption */}
        {snapshot.caption && (
          <div className="px-4 pt-2 text-xs text-gray-500">{snapshot.caption}</div>
        )}

        {/* Destination link */}
        {snapshot.link_url && (
          <div className="px-4 pt-1">
            <a
              href={snapshot.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-3 w-3" />
              <span className="truncate">{getHostname(snapshot.link_url)}</span>
            </a>
          </div>
        )}

        {/* Footer bar: page likes · date · carousel nav · ad library link */}
        <div className="mt-3 flex items-center gap-2 border-t border-gray-100 px-4 py-2">
          {snapshot.page_like_count != null && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <ThumbsUp className="h-3 w-3" />
              {snapshot.page_like_count.toLocaleString()}
            </span>
          )}

          <span className="text-xs text-gray-400">{dateLabel}</span>

          <div className="flex-1" />

          {/* Carousel nav */}
          {mediaItems.length > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() =>
                  setActiveMedia((i) => (i - 1 + mediaItems.length) % mediaItems.length)
                }
                className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Previous media"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="min-w-[2.5rem] text-center text-xs text-gray-400">
                {activeMedia + 1}/{mediaItems.length}
              </span>
              <button
                onClick={() => setActiveMedia((i) => (i + 1) % mediaItems.length)}
                className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Next media"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Ad library link */}
          <a
            href={adLibraryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-blue-600"
            title="View in Ad Library"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </article>
    </ContextMenuWrapper>
  )
}
